import { useId, useState } from 'react';
import type { DailyPoint } from '../queries/admin';

/**
 * One series, one hue, thirty columns: how many of something happened each
 * day. Hand-drawn SVG rather than a charting dependency because this is the
 * only chart in the product and it has one job.
 *
 * Columns grow from a shared baseline with a 4px rounded cap and a 2px gap of
 * surface between neighbours; the grid is a single hairline per tick; values
 * live in the tooltip and the text beside the title, never on every column.
 */

const WIDTH = 600;
const HEIGHT = 140;
const PAD = { top: 8, right: 4, bottom: 20, left: 28 };
const GAP = 2;
const CAP = 4;

const dayLabel = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

/** A clean top-of-axis: 1, 2, 5 times a power of ten, at or above the max. */
function niceMax(max: number): number {
  if (max <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(max));
  for (const step of [1, 2, 5, 10]) {
    if (step * power >= max) return step * power;
  }
  return 10 * power;
}

export function DailyChart({
  title,
  points,
  color,
  unit,
}: {
  title: string;
  points: DailyPoint[];
  /** The one series hue. Marks only; text stays in text tokens. */
  color: string;
  unit: { singular: string; plural: string };
}) {
  const [hover, setHover] = useState<number | null>(null);
  const clipId = useId();

  const total = points.reduce((sum, point) => sum + point.count, 0);
  const max = niceMax(Math.max(...points.map((point) => point.count), 0));
  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const slot = points.length > 0 ? plotW / points.length : plotW;
  const barW = Math.min(24, Math.max(2, slot - GAP));
  const y = (value: number) => PAD.top + plotH - (value / max) * plotH;
  const ticks = [0, max / 2, max];

  const active = hover !== null ? points[hover] : null;

  return (
    <figure className="card px-4 py-3.5">
      <figcaption className="flex items-baseline justify-between gap-3">
        <span className="text-micro uppercase tracking-[0.1em] text-chalk-faint">{title}</span>
        <span className="tnum text-caption text-chalk-dim" aria-live="polite">
          {active
            ? `${dayLabel.format(new Date(`${active.day}T00:00:00`))}: ${active.count.toLocaleString()} ${active.count === 1 ? unit.singular : unit.plural}`
            : `${total.toLocaleString()} ${total === 1 ? unit.singular : unit.plural} in 30 days`}
        </span>
      </figcaption>

      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="mt-2 block h-auto w-full"
        role="img"
        aria-label={`${title}, per day, last 30 days`}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <clipPath id={clipId}>
            <rect x={PAD.left} y={PAD.top} width={plotW} height={plotH} />
          </clipPath>
        </defs>

        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(tick)} y2={y(tick)} stroke="#232A33" strokeWidth={1} />
            <text x={PAD.left - 6} y={y(tick) + 3.5} textAnchor="end" fontSize={10} fill="#6B7785" className="tnum">
              {tick.toLocaleString()}
            </text>
          </g>
        ))}

        <g clipPath={`url(#${clipId})`}>
          {points.map((point, index) => {
            const x = PAD.left + index * slot + (slot - barW) / 2;
            const top = y(point.count);
            const height = PAD.top + plotH - top;
            const cap = Math.min(CAP, height);
            return (
              <g key={point.day}>
                {height > 0 ? (
                  <path
                    d={`M${x},${PAD.top + plotH} v${-(height - cap)} a${cap},${cap} 0 0 1 ${cap},${-cap} h${barW - 2 * cap} a${cap},${cap} 0 0 1 ${cap},${cap} v${height - cap} z`}
                    fill={color}
                    opacity={hover === null || hover === index ? 1 : 0.45}
                  />
                ) : null}
                {/* Hit target is the whole slot, taller than the mark. */}
                <rect
                  x={PAD.left + index * slot}
                  y={PAD.top}
                  width={slot}
                  height={plotH}
                  fill="transparent"
                  onMouseEnter={() => setHover(index)}
                />
              </g>
            );
          })}
        </g>

        {points.length > 0
          ? [0, Math.floor(points.length / 2), points.length - 1].map((index) => (
              <text
                key={index}
                x={PAD.left + index * slot + slot / 2}
                y={HEIGHT - 6}
                textAnchor={index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle'}
                fontSize={10}
                fill="#6B7785"
              >
                {dayLabel.format(new Date(`${points[index].day}T00:00:00`))}
              </text>
            ))
          : null}
      </svg>
    </figure>
  );
}
